import api from './api';
import type { PlatformOptions } from '@/types/scheduled-instances';

/**
 * Posts API Service
 *
 * API client for post management endpoints
 */

// ============================================================================
// Types
// ============================================================================

/**
 * First-class per-occurrence publishing status (Stage 2c-i, post-status
 * reconciliation). Mirrors the backend's `OccurrenceStatus` — see
 * `docs/architecture/POST-STATUS-MODEL.md`. Unlike `Post.status` (which also
 * jams in workflow states like PENDING/APPROVED), this union is
 * publishing-axis only, and never folds PARTIAL/MISSED into FAILED.
 */
export type OccurrenceStatus =
  | 'MAPPED'
  | 'SCHEDULED'
  | 'PUBLISHING'
  | 'PUBLISHED'
  | 'PARTIAL'
  | 'FAILED'
  | 'MISSED'
  | 'CANCELLED';

export interface Post {
  id: number;
  caption: string;
  title?: string;
  type?: 'SINGLE' | 'CAROUSEL' | 'VIDEO'; // Post type from backend
  /**
   * Stage 2c-ii: widened to include 'PARTIAL' | 'MISSED' — calendar-sourced
   * entries (`calendarService.getCalendarPosts`, backed by
   * `resolveOccurrenceStatus`/`latestOccurrenceStatus` server-side) populate
   * this field with the full first-class `OccurrenceStatus` vocabulary, not
   * just the original 4-value subset. See docs/architecture/POST-STATUS-MODEL.md.
   */
  status: 'UNSCHEDULED' | 'PENDING' | 'CHANGES_REQUESTED' | 'APPROVED' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'PARTIAL' | 'FAILED' | 'MISSED' | 'CANCELLED' | 'MAPPED';
  /**
   * Publishing status of the single occurrence the backend has resolved as
   * "the" one to display (e.g. latest non-cancelled) — see
   * `docs/architecture/POST-STATUS-MODEL.md`. Distinct from `status`, which
   * mixes in workflow states and can be stale for publishing (Task #182).
   */
  displayStatus?: OccurrenceStatus | null;
  contentStatus?: 'DRAFT' | 'READY'; // Content readiness status
  scheduledAt: string | null;
  publishedAt: string | null;
  postChannels: PostChannel[]; // Backend returns postChannels, not channels
  postMedia: PostMedia[]; // Backend returns postMedia, not media
  calendarPost?: CalendarPost;
  calendarMappings?: CalendarMapping[]; // Map & Shoot mappings
  aiGenerated: boolean;
  labels: string[]; // Post labels/tags
  hashtags?: string[]; // Extracted hashtags from caption
  createdAt: string;
  updatedAt: string;
  // New fields for enhanced UI
  variationsCount?: number; // Number of variations for this post
  variations?: PostVariationSummary[]; // Summary of variations
  workflowStatus?: 'UNSCHEDULED' | 'PENDING' | 'CHANGES_REQUESTED' | 'APPROVED'; // Approval workflow status
  instancesSummary?: { // Scheduled instances summary
    total: number;
    mapped: number;
    scheduled: number;
    published: number;
    failed: number;
    // First-class occurrence buckets (Stage 2c-i) — un-folded from `failed`.
    publishing?: number;
    partial?: number;
    missed?: number;
    cancelled?: number;
  };
  archivedAt?: string; // Soft delete timestamp
  // Backend fields
  visibility?: string; // Post visibility (public/private)
  metadata?: Record<string, unknown>; // Flexible metadata
  aiModel?: string; // AI model used
  aiPrompt?: string; // AI prompt used
  isEvergreen?: boolean; // Reusable content flag
  brandSpaceId?: number; // Brand space reference
  characterCounts?: Record<string, number>; // Per-platform char counts
  userId?: number; // Owner
  teamId?: number; // Team
  // Calendar mapping fields (for calendar view)
  source?: 'scheduled' | 'mapped' | 'blog'; // Indicates where post came from in calendar view ('blog' = read-only WordPress blog post)
  mappingId?: number; // Calendar mapping ID (when source is 'mapped')
  variationId?: number; // Variation ID (when source is 'mapped')
  variationTitle?: string | null; // Variation title (when source is 'mapped' with variation)
  // Blog calendar fields (when source is 'blog') — synthetic read-only post adapted from a BlogPost
  blogPostId?: number; // BlogPost ID (same value as `id` for blog entries)
  blogStatus?: 'SCHEDULED' | 'PUBLISHED' | 'FAILED'; // Blog post publish status
  wpPostUrl?: string | null; // Published WordPress post URL (null until published)
  siteIconUrl?: string | null; // WordPress site favicon (used as the synthetic channel avatar)
  siteHost?: string; // WordPress site host (used as the synthetic channel username)
}

/** Result of POST /posts/bulk-add-labels and /posts/bulk-add-hashtags. */
export interface BulkAddTagsResult {
  updatedCount: number;
  requestedCount: number;
  skipped: Array<{ postId: number; reason: string }>;
}

/** Result of POST /posts/bulk-delete: deleted count plus one entry per skipped post with a stable reason code. */
export interface BulkDeleteResult {
  deletedCount: number;
  requestedCount: number;
  skipped: Array<{
    postId: number;
    variationId: number | null;
    outcome: 'skipped';
    reasonCode: string;
    reason: string;
  }>;
}

export interface PostVariationSummary {
  id: number;
  title: string;
  caption?: string;
  contentStatus?: 'DRAFT' | 'READY';
  createdAt?: string;
  updatedAt?: string;
  postMedia?: PostMedia[];
  calendarMappings?: Array<{
    id: number;
    status: string;
  }>;
  /**
   * Per-variation scheduled-instance summary, mirroring `Post.instancesSummary`
   * above. Unioned with `calendarMappings` to resolve the variation's
   * FAILED/PUBLISHED/SCHEDULED/MAPPED badge — see `getVariationStatusCounts` /
   * `getVariationPrimaryStatus` in `utils/calendar.ts`.
   */
  instancesSummary?: {
    mapped?: number;
    scheduled: number;
    published: number;
    failed: number;
    // First-class occurrence buckets (Stage 2c-i) — un-folded from `failed`.
    publishing?: number;
    partial?: number;
    missed?: number;
    cancelled?: number;
  } | null;
  /**
   * Stage 2a follow-up (POST-STATUS-MODEL.md): server-computed single-badge
   * collapse for the variation's OWN occurrences — mirrors `Post.displayStatus`
   * above (backend: `post.service.ts#listPosts`'s `variationsWithSummary`).
   * Distinct from `getVariationPrimaryStatus` (client-side fold), which has
   * no Partial/Missed branch; PREFER this field when present.
   */
  displayStatus?: OccurrenceStatus | null;
}

export interface PostChannel {
  id: number;
  channelId: number;
  channel: {
    id: number;
    platform: string;
    platformUsername: string;
    platformAccountId?: string;
    isActive: boolean;
    profilePhotoUrl?: string;
  };
  status?: 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'PENDING';
  publishedAt?: string;
  externalPostId?: string;
  externalUrl?: string;
  error?: string;
}

export interface PostMedia {
  id: number;
  type: 'IMAGE' | 'VIDEO' | 'PDF' | 'DOCUMENT';
  originalUrl: string | null;
  thumbnailUrl: string | null;
  order: number;
  width?: number;
  height?: number;
  duration?: number;
  size?: number;
  /** Source of the media — 'GDRIVE' for deferred Google Drive items. */
  sourceType?: 'S3_PERMANENT' | 'S3_CACHED' | 'GDRIVE';
  /** Drive file ref — present when sourceType === 'GDRIVE'. */
  sourceRef?: {
    driveFileId: string;
    channelId: number;
    mimeType: string;
    sizeBytes: number;
  } | null;
}

export interface CalendarPost {
  id: number;
  postId: number;
  calendarId: number | null;
  date: string;
  scheduledTime: string;
  status: string;
  version: number;  // For optimistic locking
}

export interface CalendarMapping {
  id: number;
  calendarId: number;
  plannedDate: string;
  status: 'MAPPED' | 'SCHEDULED' | 'PUBLISHED' | 'FAILED';
}

export interface BulkRescheduleConflict {
  postId: number;
  reason: 'already_scheduled';
  channelId: number;
  channelName: string;
  existingScheduledAt: string;
  existingInstanceId: number;
  existingCalendarMappingId: number;
  timeZone: string;
}

export interface BulkRescheduleResult {
  success: boolean;
  rescheduledCount: number;
  requestedCount: number;
  skippedInstances?: number;
  /** Posts NOT moved because they already have a live schedule on that day. */
  skippedDueToConflict?: BulkRescheduleConflict[];
}

export interface PostStats {
  total: number;
  byStatus: {
    UNSCHEDULED: number;
    SCHEDULED: number;
    PUBLISHING: number;
    PUBLISHED: number;
    FAILED: number;
  };
  recentActivity: {
    last24h: number;
    last7d: number;
  };
  queueStatus: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
  };
}

export type PublishingStatus = '' | 'all' | 'unmapped' | 'mapped' | 'scheduled' | 'publishing' | 'published' | 'failed';

export type WorkflowStatus = 'all' | 'PENDING' | 'CHANGES_REQUESTED' | 'APPROVED';

export interface PostFilters {
  status: string; // 'ALL' | 'UNSCHEDULED' | 'SCHEDULED' | etc.
  platforms: string[] | null; // Array of platform names, or null for all platforms
  dateFrom: string | null;
  dateTo: string | null;
  search: string;
  contentStatus?: 'DRAFT' | 'READY' | null; // User-controlled content status
  publishingStatus?: PublishingStatus; // Publishing status filter (inferred from mappings/instances)
  tags?: string; // Comma-separated tag filter
  channel?: string; // Channel filter (ID or 'ALL')
  sortBy?: 'newest' | 'oldest' | 'scheduled_asc' | 'scheduled_desc'; // Sort order
  mediaType?: 'all' | 'image' | 'video' | 'pdf' | 'none' | null; // Filter by media type
  aiGenerated?: boolean; // Filter to only show AI-generated posts
  workflowStatus?: WorkflowStatus | null; // Approval workflow status filter (agency only)
  /** Only these posts (max 100, still scoped to the team). Used by the calendar's newly released view. */
  postIds?: number[];
}

export interface PostListResponse {
  success: boolean;
  data: Post[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Server-side draft-autosave wire payload — an opaque JSON object owned by
 * the composer (see `ComposerDraft` in `hooks/usePostComposer.ts`). Kept
 * generic here (rather than importing the composer's own type) to avoid a
 * services→hooks dependency; callers serialize/deserialize their own typed
 * draft shape into/out of this format.
 */
export type DraftAutosavePayload = Record<string, unknown>;

/** Result of GET /api/v2/posts/create-requests/:key. `response` is the original response body. */
export type CreateRequestLookup =
  | {
      status: 'completed';
      response: unknown;
      postIds: number[];
      responseStatus?: number;
      /** 'created_with_error': the post(s) committed but the original response was an error body. */
      outcome?: 'created' | 'created_with_error';
    }
  | { status: 'in_progress'; postIds?: number[] }
  | { status: 'failed' }
  | { status: 'not_found' };

/** A saved server-side draft-autosave record, as returned by GET. */
export interface DraftAutosaveRecord {
  payload: DraftAutosavePayload;
  // ISO-8601 timestamp — the client-device clock value echoed back by the
  // server (NOT server-authoritative; the local-vs-server "newest wins"
  // merge compares each device's own clock).
  savedAt: string;
}

export interface CalendarData {
  [date: string]: Post[];
}

// ============================================================================
// API Methods
// ============================================================================

export const postsApi = {
  /**
   * List posts with filters and pagination
   */
  list: async (
    filters: PostFilters & { page: number; limit: number }
  ): Promise<PostListResponse> => {
    const params = new URLSearchParams();

    // Add filters (skip 'ALL' values)
    if (filters.status && filters.status !== 'ALL') {
      params.append('status', filters.status);
    }
    // Add multiple platforms (if not null)
    if (filters.platforms && filters.platforms.length > 0) {
      filters.platforms.forEach((platform) => {
        params.append('platforms', platform);
      });
    }
    if (filters.dateFrom) {
      params.append('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      params.append('dateTo', filters.dateTo);
    }
    if (filters.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }

    // Add content status filter
    if (filters.contentStatus) {
      params.append('contentStatus', filters.contentStatus);
    }

    // Add publishing status filter
    if (filters.publishingStatus && filters.publishingStatus !== 'all') {
      params.append('publishingStatus', filters.publishingStatus);
    }

    // Add tags filter
    if (filters.tags && filters.tags.trim()) {
      params.append('tags', filters.tags.trim());
    }

    // Add channel filter
    if (filters.channel && filters.channel !== 'ALL') {
      params.append('channel', filters.channel);
    }

    // Add sortBy filter
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }

    // Add mediaType filter
    if (filters.mediaType && filters.mediaType !== 'all') {
      params.append('mediaType', filters.mediaType);
    }

    // Add aiGenerated filter
    if (filters.aiGenerated !== undefined) {
      params.append('aiGenerated', String(filters.aiGenerated));
    }

    // Add workflowStatus filter (approval workflow)
    if (filters.workflowStatus && filters.workflowStatus !== 'all') {
      params.append('workflowStatus', filters.workflowStatus);
    }

    // Add explicit post ids (comma separated, max 100)
    if (filters.postIds && filters.postIds.length > 0) {
      params.append('postIds', filters.postIds.slice(0, 100).join(','));
    }

    // Add pagination
    params.append('page', filters.page.toString());
    params.append('limit', filters.limit.toString());

    const { data } = await api.get<PostListResponse>(`/api/posts?${params.toString()}`);
    return data;
  },

  /**
   * Get post statistics
   */
  getStats: async (): Promise<PostStats> => {
    const { data } = await api.get<{ success: boolean; data: PostStats }>('/api/posts/stats');
    return data.data;
  },

  /**
   * Get calendar view (posts grouped by date)
   */
  getCalendar: async (month: string, platform?: string, timezone?: string): Promise<CalendarData> => {
    const params = new URLSearchParams({ month });
    if (platform && platform !== 'ALL') {
      params.append('platform', platform);
    }
    // Pass the user's timezone so the (occurrence-centric) backend buckets each
    // occurrence into the same day key the grid looks up — mirrors the
    // multi-calendar path's `timezone` param. Omitted → backend cascade default.
    if (timezone) {
      params.append('timezone', timezone);
    }

    const { data } = await api.get<{ success: boolean; data: CalendarData }>(
      `/api/posts/calendar?${params.toString()}`
    );
    return data.data;
  },

  /**
   * Get single post by ID
   */
  getById: async (postId: number, signal?: AbortSignal): Promise<Post> => {
    const { data } = await api.get<{ success: boolean; data: Post }>(`/api/posts/${postId}`, { signal });
    return data.data;
  },

  /**
   * Create new post
   */
  create: async (postData: {
    title?: string;
    caption: string;
    channelIds: number[];
    mediaIds?: string[];
    scheduledAt?: string;
    aiGenerated?: boolean;
    metadata?: Record<string, unknown>;
    channelOptions?: Record<number, PlatformOptions>;
  }): Promise<Post> => {
    const { data } = await api.post<{ success: boolean; data: Post }>('/api/posts', postData);
    return data.data;
  },

  /**
   * Update post
   */
  update: async (
    postId: number,
    postData: {
      title?: string;
      caption?: string;
      channelIds?: number[];
      scheduledAt?: string | null;  // null to clear scheduled time
      status?: string;
      metadata?: Record<string, unknown>;
      version?: number;  // For optimistic locking
    }
  ): Promise<Post> => {
    const { data } = await api.put<{ success: boolean; data: Post }>(
      `/api/posts/${postId}`,
      postData
    );
    return data.data;
  },

  /**
   * Delete post (soft delete)
   */
  delete: async (postId: number): Promise<void> => {
    await api.delete(`/api/posts/${postId}`);
  },

  /**
   * Duplicate post (creates DRAFT copy)
   */
  duplicate: async (postId: number): Promise<Post> => {
    const { data } = await api.post<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/duplicate`
    );
    return data.data;
  },

  /**
   * Bulk delete posts
   */
  bulkDelete: async (postIds: number[]): Promise<BulkDeleteResult> => {
    const { data } = await api.post<{ success: boolean; data: BulkDeleteResult }>('/api/posts/bulk-delete', { postIds });
    return data.data;
  },

  /**
   * Bulk reschedule posts
   */
  bulkReschedule: async (postIds: number[], scheduledFor: string): Promise<BulkRescheduleResult> => {
    const response = await api.post<{ success: boolean; message?: string; data: BulkRescheduleResult }>(
      '/api/posts/bulk-reschedule',
      { postIds, scheduledFor }
    );
    return response.data.data;
  },

  /**
   * Bulk add labels to posts
   */
  bulkAddLabels: async (postIds: number[], labels: string[]): Promise<BulkAddTagsResult> => {
    const response = await api.post<{ success: boolean; data: BulkAddTagsResult }>(
      '/api/posts/bulk-add-labels', { postIds, labels }
    );
    return response.data.data;
  },

  /**
   * Bulk add hashtags to posts (appends to caption)
   */
  bulkAddHashtags: async (postIds: number[], hashtags: string[]): Promise<BulkAddTagsResult> => {
    const response = await api.post<{ success: boolean; data: BulkAddTagsResult }>(
      '/api/posts/bulk-add-hashtags', { postIds, hashtags }
    );
    return response.data.data;
  },

  /**
   * Export selected posts as CSV download
   */
  exportPosts: async (postIds: number[]): Promise<void> => {
    const response = await api.post('/api/posts/export', { postIds }, {
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `posts-export-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Schedule post for publishing
   */
  schedule: async (postId: number, scheduledAt: string): Promise<Post> => {
    const { data } = await api.post<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/schedule`,
      { scheduledAt }
    );
    return data.data;
  },

  /**
   * Publish post immediately
   */
  publish: async (postId: number): Promise<Post> => {
    const { data } = await api.post<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/publish`
    );
    return data.data;
  },

  /**
   * Retry failed post
   */
  retry: async (postId: number): Promise<Post> => {
    const { data } = await api.post<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/retry`
    );
    return data.data;
  },

  /**
   * Mark post as ready
   */
  markReady: async (postId: number): Promise<Post> => {
    const { data } = await api.patch<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/status`,
      { contentStatus: 'READY' }
    );
    return data.data;
  },

  /**
   * Mark post as draft
   */
  markDraft: async (postId: number): Promise<Post> => {
    const { data } = await api.patch<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/status`,
      { contentStatus: 'DRAFT' }
    );
    return data.data;
  },

  /**
   * Bulk update post status (contentStatus: DRAFT/READY)
   */
  bulkUpdateStatus: async (postIds: number[], contentStatus: 'DRAFT' | 'READY'): Promise<void> => {
    await api.patch('/api/posts/bulk/status', { postIds, contentStatus });
  },

  /**
   * Save post and mark as ready in one operation
   */
  saveAndMarkReady: async (
    postId: number,
    updateData?: {
      caption?: string;
      channelIds?: number[];
      mediaIds?: string[];
      labels?: string[];
      scheduledAt?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<Post> => {
    const { data } = await api.post<{ success: boolean; data: Post }>(
      `/api/posts/${postId}/save-and-mark-ready`,
      updateData || {}
    );
    return data.data;
  },

  /**
   * ============================================================================
   * DRAFT AUTOSAVE (server-side mirror of the composer's local autosave)
   * ============================================================================
   * Single-slot autosave per user+team+contextKey, complementing the
   * localStorage autosave (`useAutosave`) already used by the composer.
   * `contextKey` is `'new'` for a brand-new post, or an existing post id
   * (as a string) when editing. teamId/userId are always derived
   * server-side from the authenticated session — never sent from the
   * client.
   */

  /**
   * Create or replace the caller's draft autosave for a composer context.
   */
  putDraftAutosave: async (params: {
    contextKey: string;
    payload: DraftAutosavePayload;
    savedAt: number;
    /** Idempotency-Key of the create this 'new' draft will be saved with (server clears the slot only on a match). */
    createIntentKey?: string;
  }): Promise<{ success: boolean; savedAt: string }> => {
    const { data } = await api.put<{ success: boolean; savedAt: string }>(
      '/api/v2/posts/draft-autosave',
      params
    );
    return data;
  },

  /**
   * Outcome of a post create by its Idempotency-Key (used after a timeout / dropped connection).
   */
  getCreateRequest: async (key: string): Promise<CreateRequestLookup> => {
    const { data } = await api.get<{ success: boolean; data: CreateRequestLookup }>(
      `/api/v2/posts/create-requests/${encodeURIComponent(key)}`
    );
    return data.data;
  },

  /**
   * Get the caller's draft autosave for a composer context, if any.
   */
  getDraftAutosave: async (
    contextKey: string
  ): Promise<{ success: boolean; data: DraftAutosaveRecord | null }> => {
    const { data } = await api.get<{ success: boolean; data: DraftAutosaveRecord | null }>(
      `/api/v2/posts/draft-autosave?contextKey=${encodeURIComponent(contextKey)}`
    );
    return data;
  },

  /**
   * Delete the caller's draft autosave for a composer context (idempotent —
   * safe to call even when no row exists for this context).
   */
  deleteDraftAutosave: async (contextKey: string): Promise<{ success: boolean }> => {
    const { data } = await api.delete<{ success: boolean }>(
      `/api/v2/posts/draft-autosave?contextKey=${encodeURIComponent(contextKey)}`
    );
    return data;
  },
};
